import { useState } from "react";
import swal from "sweetalert";

import useUsoundPaymentsProducts from "../../../Hooks/General/useUsoundPaymentsProducts";
import useCurrencies from "../../../Hooks/General/useCurrencies";
import useLicenses from "../../../Hooks/CS/useLicenses";
import { hideModal } from "../../../Utilities/General/BootstrapApi";
import UsoundPaymentsService from "../../../Services/UsoundPayments/UsoundPaymentsService";
import LicenseService from "../../../Services/Licenses/LicenseService";

export default function AddFreemiumFeatures({ license, setSerial }) {
    const products = useUsoundPaymentsProducts()
    const currencies = useCurrencies()
    const { licenses, setLicenses } = useLicenses()
    const pricingCountry = license?.country || (
        process.env.REACT_APP_ENVIRONMENT === 'testing'
            ? license?.code_object?.country_iso
            : ''
    )
    const [orderDetails, setOrderDetails] = useState([])
    const [paymentMethod, setPaymentMethod] = useState('')
    const [tiendaNubeIds, setTiendaNubeIds] = useState({ tiendanube_order_id: '', tiendanube_transaction_id: '' })
    const [amount, setAmount] = useState('')
    const [currency, setCurrency] = useState('')
    const [manualLinkReason, setManualLinkReason] = useState('')
    const [otherManualLinkReason, setOtherManualLinkReason] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const resolvedManualLinkReason = manualLinkReason === 'Otro'
        ? otherManualLinkReason.trim() || 'Otro'
        : manualLinkReason

    const handlePricingChange = (e, name) => {
        if (e.target.value === 'delete') {
            setOrderDetails(orderDetails.filter(detail => detail.name !== name))
            return
        }
        setOrderDetails([
            ...orderDetails.filter(detail => detail.name !== name),
            { name, pricing: e.target.value }
        ])
    }

    const handleReset = () => {
        hideModal('addFF')
        document.getElementById('odVal').classList.add('d-none')
        document.querySelectorAll('.selTest').forEach(select => { select.value = 'delete' })
        setOrderDetails([])
        setPaymentMethod('')
        setTiendaNubeIds({ tiendanube_order_id: '', tiendanube_transaction_id: '' })
        setAmount('')
        setCurrency('')
        setManualLinkReason('')
        setOtherManualLinkReason('')
    }

    const validate = () => {
        const detailsError = document.getElementById('odVal')
        if (orderDetails.length === 0) {
            detailsError.classList.remove('d-none')
            return false
        }
        detailsError.classList.add('d-none')
        if (!paymentMethod) {
            swal({
                title: 'Falta el medio de pago',
                text: 'Seleccione Tienda Nube, Efectivo/Otros u otro medio de pago.',
                icon: 'warning'
            })
            return false
        }
        if (paymentMethod === 'cash' && (!amount || !currency)) {
            swal({
                title: 'Faltan importe o moneda',
                text: 'Ingrese ambos datos para registrar la orden manual.',
                icon: 'warning'
            })
            return false
        }
        if (paymentMethod !== '' && paymentMethod !== 'tiendanube') {
            swal({
                title: 'Medio de pago aun no implementado',
                text: `${paymentMethod === 'stripe' ? 'Stripe' : 'Rebill'} todavia no esta disponible para enlazar licencias.`,
                icon: 'info'
            })
            return false
        }
        if (paymentMethod === 'tiendanube' && !tiendaNubeIds.tiendanube_order_id) {
            swal({
                title: 'Falta el ID de orden de Tienda Nube',
                text: 'Ingrese el ID técnico de la orden. El número visible, por ejemplo #734, no sirve para consultarla.',
                icon: 'warning'
            })
            return false
        }
        return true
    }

    const refreshLicense = successMessage => {
        LicenseService.getLicense(license.id).then(data => {
            setLicenses([...licenses.filter(item => item.id !== license.id), data])
            setSerial(data)
            handleReset()
            swal(successMessage)
        })
    }

    const handleSubmit = () => {
        if (!validate()) return
        const isCashPayment = paymentMethod === 'cash'
        swal({
            title: isCashPayment ? '¿Guardar estudios mediante una orden manual?' : '¿Verificar la venta de Tienda Nube?',
            text: isCashPayment ? 'Efectivo/Otros habilita la licencia mediante el flujo de Order.' : 'Se verificará que la orden esté pagada en Tienda Nube antes de habilitar la licencia.',
            icon: 'warning',
            buttons: ['Cancelar', 'Confirmar']
        }).then(confirm => {
            if (!confirm) return
            setIsSubmitting(true)
            console.info('[license-activation] submit', {
                licenseId: license?.id,
                paymentMethod,
                pricingIds: orderDetails.map(detail => parseInt(detail.pricing))
            })
            const request = isCashPayment ?
                UsoundPaymentsService.saveNewOrder({
                    amount: parseInt(amount),
                    currency,
                    origin: 'dashboard-features-sale',
                    license_id: license?.id.toString(),
                    orderdetail_set: orderDetails.map(detail => ({
                        pricing: parseInt(detail.pricing),
                        tag: license.code_code
                    }))
                }) :
                UsoundPaymentsService.linkTiendaNubePayment({
                    license_id: license?.id.toString(),
                    pricing_ids: orderDetails.map(detail => parseInt(detail.pricing)),
                    manual_link_reason: resolvedManualLinkReason,
                    ...tiendaNubeIds
                })

            request.then(payment => {
                console.info('[license-activation] request completed', {
                    licenseId: license?.id,
                    tiendanubeOrderId: payment?.tiendanube_order_id || null,
                    orderId: payment?.id
                })
                refreshLicense({
                    title: isCashPayment ? 'Licencia habilitada' : payment.created ? 'Licencia enlazada' : 'Enlace ya existente',
                    text: isCashPayment ? 'La orden manual fue creada y la licencia fue habilitada.' : `Orden Tienda Nube ${payment.tiendanube_order_id} enlazada a la licencia.`,
                    icon: 'success'
                })
            }).catch(err => {
                console.error('[license-activation] request failed', {
                    licenseId: license?.id,
                    message: err.message
                })
                swal({
                    title: isCashPayment ? 'No se pudo crear la orden manual' : 'No se pudo verificar la orden de Tienda Nube',
                    text: err.message,
                    icon: 'error'
                })
            }).finally(() => setIsSubmitting(false))
        })
    }

    return (
        <div className="modal fade" id="addFF">
            <div className="modal-dialog modal-xl modal-dialog-scrollable shadow">
                <div className="modal-content">
                    <div className="modal-header">
                        <h3 className="modal-title text-info">Agregar estudios y enlazar pago</h3>
                        <button type="button" className="btn-close" aria-label="Close" onClick={handleReset}></button>
                    </div>
                    <div className="modal-body add-freemium-features-body d-flex flex-wrap gap-4">
                        <div className="col-12 col-lg-6 add-freemium-products-column">
                            <div className="add-freemium-products-list">
                                <table className="table table-hover text-center">
                                <thead><tr><th>Nombre</th><th>Periodo</th></tr></thead>
                                <tbody>
                                    {products?.map(product => {
                                        if (license?.modelfreemium_set.filter(feature => feature.feature === product.sku && feature.lifetime).length > 0) return null
                                        return (
                                            <tr key={product.id}>
                                                <td>{product.name}</td>
                                                <td>
                                                    <select className="form-select selTest" onChange={e => handlePricingChange(e, product.name)}>
                                                        <option value="delete">No agregado</option>
                                                        {product.pricing_set.filter(pricing => pricing.country_iso === pricingCountry).map(pricing => (
                                                            <option key={pricing.id} value={pricing.id}>
                                                                {pricing.period === 12 ? 'Anual' : pricing.period === 6 ? '6 meses' : pricing.period === 1 ? 'Mensual' : 'De por vida'}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="col-12 col-lg-5 border border-muted rounded p-4">
                            <h4 className="text-center text-info">Medio de pago</h4>
                            <select className="form-select" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                                <option value="">Seleccione un medio de pago</option>
                                <option value="stripe">Stripe - no implementado todavia</option>
                                <option value="rebill">Rebill - no implementado todavia</option>
                                <option value="tiendanube">Tienda Nube</option>
                                <option value="cash">Efectivo/Otros</option>
                            </select>

                            {paymentMethod === 'cash' &&
                                <div className="mt-3">
                                    <p className="small text-muted mb-2">Se registrará mediante el flujo manual de Order.</p>
                                    <label className="form-label small mb-1">Importe</label>
                                    <input type="number" min="0" className="form-control mb-2" value={amount} onChange={e => setAmount(e.target.value)} />
                                    <label className="form-label small mb-1">Moneda</label>
                                    <select className="form-select" value={currency} onChange={e => setCurrency(e.target.value)}>
                                        <option value="" disabled>Seleccione</option>
                                        {currencies?.map(item => <option key={item.id} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            }
                            {paymentMethod === 'tiendanube' &&
                                <div className="mt-3">
                                    <p className="small text-muted mb-2">Se verificará la orden pagada en Tienda Nube. El ID técnico de orden es obligatorio; la transacción es opcional.</p>
                                    <label className="form-label small mb-1">ID técnico de orden Tienda Nube</label>
                                    <input className="form-control mb-2" value={tiendaNubeIds.tiendanube_order_id} onChange={e => setTiendaNubeIds({ ...tiendaNubeIds, tiendanube_order_id: e.target.value.trim() })} />
                                    <label className="form-label small mb-1">ID de transacción Tienda Nube (opcional)</label>
                                    <input className="form-control" value={tiendaNubeIds.tiendanube_transaction_id} onChange={e => setTiendaNubeIds({ ...tiendaNubeIds, tiendanube_transaction_id: e.target.value.trim() })} />
                                    <label className="form-label small mb-1 mt-3">Motivo del enlace manual (opcional)</label>
                                    <select className="form-select" value={manualLinkReason} onChange={e => setManualLinkReason(e.target.value)}>
                                        <option value="">Seleccione un motivo</option>
                                        <option value="Paga un tercero">Paga un tercero</option>
                                        <option value="Financiación en cuotas">Financiación en cuotas</option>
                                        <option value="Descuento en tiendaonline">Descuento en tiendaonline</option>
                                        <option value="Otro">Otro</option>
                                    </select>
                                    {manualLinkReason === 'Otro' &&
                                        <input
                                            className="form-control mt-2"
                                            placeholder="Escriba el motivo (opcional)"
                                            value={otherManualLinkReason}
                                            onChange={e => setOtherManualLinkReason(e.target.value)}
                                        />
                                    }
                                </div>
                            }
                            {paymentMethod && paymentMethod !== 'tiendanube' && paymentMethod !== 'cash' &&
                                <p className="small text-warning mt-3 mb-0">Este medio de pago se mostrará aquí, pero todavía no puede procesarse.</p>
                            }

                            <h4 className="mt-4 text-center text-info">Vista previa</h4>
                            <ul className="list-group list-group-flush">
                                {orderDetails.length === 0 ?
                                    <li className="list-group-item text-center">No hay estudios agregados.</li> :
                                    orderDetails.slice().sort((a, b) => a.name.localeCompare(b.name)).map(detail => {
                                        const product = products?.find(item => item.name === detail.name)
                                        const period = product?.pricing_set.find(item => item.id === parseInt(detail.pricing))?.period
                                        return (
                                            <li key={detail.pricing} className="list-group-item d-flex justify-content-between">
                                                <span>{detail.name}</span>
                                                <span>{period === 12 ? 'Anual' : period === 6 ? '6 meses' : period === 1 ? 'Mensual' : 'De por vida'}</span>
                                            </li>
                                        )
                                    })}
                            </ul>
                            <small id="odVal" className="d-none text-danger">* No puede guardar una licencia sin estudios agregados.</small>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={handleReset}>CANCELAR</button>
                        <button type="button" className="btn btn-info text-white" onClick={handleSubmit} disabled={isSubmitting || !paymentMethod}>
                            {isSubmitting ? 'ENLAZANDO...' : 'CONFIRMAR'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
